import { GetWorkflowNavigationLogs } from "./Workflows/WorkflowNavigationDiagnostics";
import {
    SanitizeClientDiagnosticText,
    SanitizeDiagnosticValue,
} from "./ClientDiagnosticSanitization";

export type ClientDiagnosticLevel = "debug" | "info" | "warn" | "error";

declare const CLIENT_BUILD_ID: string;

export interface IClientDiagnosticRecord {
    timestamp: string;
    clientBuildId: string;
    category: string;
    event: string;
    level: ClientDiagnosticLevel;
    details?: Record<string, unknown>;
    location: string;
    isOnline: boolean;
    visibilityState: string;
}

const storageKey = "motw.clientDiagnostics";
const maxRecordCount = 500;
const maxDiagnosticReportLength = 48000;

export function LogClientDiagnostic(
    category: string,
    event: string,
    details?: Record<string, unknown>,
    level: ClientDiagnosticLevel = "info"
) {
    const record: IClientDiagnosticRecord = {
        timestamp: new Date().toISOString(),
        clientBuildId:
            typeof CLIENT_BUILD_ID === "undefined"
                ? "unknown"
                : CLIENT_BUILD_ID,
        category,
        event,
        level,
        details,
        location: window.location.pathname,
        isOnline: window.navigator.onLine,
        visibilityState: document.visibilityState,
    };

    try {
        const records = GetClientDiagnosticLogs();
        records.push(record);
        const recentRecords = records.slice(-maxRecordCount);
        localStorage.setItem(storageKey, JSON.stringify(recentRecords));
        ExposeClientDiagnosticLogs(recentRecords);
    } catch (error) {
        console.warn("[ClientDiagnostics] failed to store log", error);
    }

    const consoleMethod = GetConsoleMethod(level);
    if (details) {
        consoleMethod(`[${category}] ${event}`, details);
    } else {
        consoleMethod(`[${category}] ${event}`);
    }
}

export function GetClientDiagnosticLogs(): IClientDiagnosticRecord[] {
    try {
        const records = JSON.parse(localStorage.getItem(storageKey) || "[]");
        return Array.isArray(records) ? records : [];
    } catch (_error) {
        return [];
    }
}

export function GetClientDiagnosticLogsText() {
    return JSON.stringify(GetClientDiagnosticLogs(), null, 2);
}

export function GetClientDiagnosticReportText() {
    const records = [
        ...GetWorkflowNavigationLogs(),
        ...GetClientDiagnosticLogs(),
    ]
        .filter(HasTimestamp)
        .sort((left, right) => left.timestamp.localeCompare(right.timestamp));
    const sanitizedRecords = records.map((record) =>
        SanitizeDiagnosticValue(record)
    );

    for (let firstRecordIndex = 0; firstRecordIndex < records.length; firstRecordIndex++) {
        const text = JSON.stringify(
            sanitizedRecords.slice(firstRecordIndex),
            null,
            2
        );

        if (text.length <= maxDiagnosticReportLength) {
            return text;
        }
    }

    return "[]";
}

function HasTimestamp(value: unknown): value is { timestamp: string } {
    return (
        typeof value === "object" &&
        value !== null &&
        typeof (value as { timestamp?: unknown }).timestamp === "string"
    );
}

export function ClearClientDiagnosticLogs() {
    try {
        localStorage.removeItem(storageKey);
        ExposeClientDiagnosticLogs([]);
    } catch (error) {
        console.warn("[ClientDiagnostics] failed to clear logs", error);
    }
}

export function GetClientErrorDetails(error: unknown) {
    if (error instanceof Error) {
        return {
            name: error.name,
            message: LimitDiagnosticText(error.message),
            stack: LimitDiagnosticText(error.stack),
        };
    }

    if (typeof error === "object" && error !== null) {
        const errorLike = error as {
            code?: unknown;
            error?: unknown;
            message?: unknown;
            name?: unknown;
        };
        const details: Record<string, string> = {};

        if (typeof errorLike.name === "string") {
            details.name = errorLike.name;
        }
        if (typeof errorLike.message === "string") {
            details.message =
                LimitDiagnosticText(errorLike.message) ?? "";
        }
        if (typeof errorLike.code === "string") {
            details.code = errorLike.code;
        }
        if (typeof errorLike.error === "string") {
            details.error = errorLike.error;
        }

        if (Object.keys(details).length > 0) {
            return details;
        }
    }

    return { message: String(error) };
}

function LimitDiagnosticText(text: string | undefined) {
    if (!text || text.length <= 4000) {
        return text;
    }

    return `${text.slice(0, 4000)}... [truncated]`;
}

function ExposeClientDiagnosticLogs(records: IClientDiagnosticRecord[]) {
    (window as any).tfaClientDiagnostics = records;
    (window as any).tfaClearClientDiagnostics = ClearClientDiagnosticLogs;
}

function GetConsoleMethod(level: ClientDiagnosticLevel) {
    if (level === "error") return console.error;
    if (level === "warn") return console.warn;
    if (level === "debug") return console.debug;
    return console.log;
}
